/**
 * Prédicat de visibilité d'un panneau dans le PanelPicker.
 *
 * Extrait de `PanelPicker` pour être TESTABLE, et rebranché sur les
 * capabilities — la source que lit aussi le rail du Bureau
 * (`ecransDuBureau.ts`). Le picker tenait sa propre porte, et
 * elle posait la mauvaise question.
 */
import type { Permission } from "@/core/auth";
import type { PanelDefinition } from "./PanelRegistry";

/** Les portes que le picker consulte, résolues. */
export type PanelGates = {
  /** Permissions FGA de l'utilisateur sur le compte actif (déjà résolues). */
  permissions: ReadonlySet<Permission>;
  /**
   * Capabilities de l'org/utilisateur — `useCapabilities()`. C'est la MÊME
   * source que le rail du Bureau : c'est tout l'intérêt.
   */
  capabilities: ReadonlySet<string>;
  canUseMetrics: boolean;
  canUseLab: boolean;
};

/**
 * Ce panneau doit-il être proposé dans le picker ?
 *
 * 🪤 La porte d'une app se lit sur sa CAPABILITY, jamais sur un e-mail. Avant,
 * `requiresChannelAccess` était résolu ici par `useCanUseChannels` (l'e-mail de
 * Kayla en dur, `channels/access.ts`) tandis que l'Explorateur (parti le
 * 08-10-2026) passait par `useCapabilities` : un membre à qui l'accès Twitter était accordé trouvait
 * l'app d'un côté et pas de l'autre, sans qu'aucun écran ne le dise.
 */
export function isPanelVisible(
  panel: PanelDefinition,
  gates: PanelGates,
): boolean {
  if (panel.hidden) return false;
  if (panel.requiredPermission && !gates.permissions.has(panel.requiredPermission)) {
    return false;
  }
  if (panel.requiredCapability && !gates.capabilities.has(panel.requiredCapability)) {
    return false;
  }
  // Métriques et Lab restent des portes « qui es-tu » assumées (Kayla /
  // superadmin), pas des apps qu'on peut se voir accorder : elles ne bougent pas.
  if (panel.requiresMetricsAccess && !gates.canUseMetrics) return false;
  if (panel.requiresLabAccess && !gates.canUseLab) return false;
  return true;
}
